// /////////////////////////////////////////////////////////////
// [ファイル] InlineEditField.tsx
// [概要] 共通コンポーネント：詳細画面の項目をその場で編集する（項目ごとのインライン編集）。
//        表示のしかたは InlineEditModeProvider の mode で切り替える。
//          view  … 表示だけ（閲覧中。鉛筆も下線も出さない）
//          edit  … 編集モード。編集できる項目に鉛筆と点線の下線を出し、値か鉛筆を押すとその項目の入力欄が開く
//          hover … Provider の無い画面の既定。値にマウスを乗せると薄い背景と鉛筆が出る（タッチ端末は鉛筆を薄く常時表示）
//        入力欄は値の真上に浮かせて重ねる（Menu と同じく画面の上に出す）ので、開いても下の項目の高さや並びは変わらない。
//        保存は ✓ / Enter（複数行は Ctrl+Enter）、取り消しは ✗ / Esc。フォーカスが外れても自動保存はしない
//        （誤操作でそのまま本番データが変わるのを防ぐ）。入力欄の外を押すと、変えていなければ閉じ、変えていれば
//        閉じずに保存か取り消しを促す。値が変わっていなければ保存しない。
//        保存中・失敗は入力欄の中に表示し、失敗時は入力を残す。成功・失敗の通知は呼び出し側の onSave で行う。
//        1 項目で完結する値に使う（フェーズ・顧客・筆など条件や履歴を伴う操作はダイアログのまま）。
// /////////////////////////////////////////////////////////////
import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { Autocomplete, Box, Chip, ClickAwayListener, IconButton, MenuItem, Paper, Popper, TextField, Tooltip, Typography } from '@mui/material';
import * as Icons from '../constants/icons';
import LoadingView from './LoadingView';
import { INLINE_EDIT_CANCEL_BUTTON_SX, INLINE_EDIT_ICON_BUTTON_SX, INLINE_EDIT_SAVE_BUTTON_SX } from '../constants/buttonStyles';

// 編集モード（画面の「編集モード」ボタンで view ⇔ edit を切り替える）。onEditingChange は入力欄を開いた・閉じたときに呼ぶ
// （保存していない入力があるまま編集モードを終えようとしたときの確認に使う。参照が変わらないよう useCallback で渡す）
export interface InlineEditMode {
  mode: 'view' | 'edit' | 'hover';
  onEditingChange?: (editing: boolean) => void;
}
const InlineEditModeContext = createContext<InlineEditMode>({ mode: 'hover' });
export const InlineEditModeProvider = InlineEditModeContext.Provider;
// 編集用のボタン (追加・削除・フェーズ変更など) を出すかどうか。閲覧中 (view) だけ false
export const useCanEdit = (): boolean => useContext(InlineEditModeContext).mode !== 'view';

export interface InlineEditorArgs<T> {
  draft: T;
  setDraft: (v: T) => void;
  saving: boolean;
  // 入力欄の onKeyDown に付ける（Enter で保存・Esc で取り消し。複数行は Ctrl+Enter で保存。変換中の Enter は無視）
  onKeyDown: (e: React.KeyboardEvent) => void;
}

interface InlineEditProps<T> {
  value: T;
  display: React.ReactNode;
  onSave: (next: T) => Promise<void>; // 失敗時は Error を投げる（message を項目の中に出し、入力を残す）
  renderEditor: (args: InlineEditorArgs<T>) => React.ReactNode;
  validate?: (draft: T) => string | null;
  isEqual?: (a: T, b: T) => boolean;
  multiline?: boolean;
  disabled?: boolean; // 鉛筆を出さない（編集できない項目）
  label?: string; // 鉛筆のツールチップ「◯◯を編集」
  editMinWidth?: number; // 編集中の最小幅（見出しなど狭い場所で入力欄が潰れないように）
}

export function InlineEdit<T>({
  value, display, onSave, renderEditor, validate, isEqual = (a, b) => a === b, multiline, disabled, label, editMinWidth,
}: InlineEditProps<T>) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<T>(value);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [anchorWidth, setAnchorWidth] = useState(0);
  const anchorRef = useRef<HTMLDivElement | null>(null);
  const { mode, onEditingChange } = useContext(InlineEditModeContext);

  // 編集モードを終えたら開いている入力欄も閉じる（確認は画面側で済ませている）
  useEffect(() => {
    if (mode === 'view') { setEditing(false); setError(null); }
  }, [mode]);
  // 入力欄を開いている間だけ画面へ知らせる
  useEffect(() => {
    if (!editing || !onEditingChange) return;
    onEditingChange(true);
    return () => onEditingChange(false);
  }, [editing, onEditingChange]);

  const start = () => { setDraft(value); setError(null); setEditing(true); };
  const cancel = () => {
    if (saving) return;
    setEditing(false); setError(null);
  };
  const save = async () => {
    if (saving) return;
    const msg = validate ? validate(draft) : null;
    if (msg) { setError(msg); return; }
    if (isEqual(draft, value)) { setEditing(false); return; }
    setSaving(true); setError(null);
    try {
      await onSave(draft);
      setEditing(false);
    } catch (e: any) {
      setError(e?.message || '保存に失敗しました');
    } finally {
      setSaving(false);
    }
  };
  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.preventDefault(); e.stopPropagation(); cancel();
    } else if (e.key === 'Enter' && !(e.nativeEvent as KeyboardEvent).isComposing && e.keyCode !== 229 && (!multiline || e.ctrlKey || e.metaKey)) {
      e.preventDefault(); save();
    }
  };
  const editLabel = label ? `${label}を編集` : '編集';
  // 閲覧中 (view) と編集できない項目は表示だけ
  const editable = !disabled && mode !== 'view';
  // 値の上にマウスを乗せると薄い背景が出て、値そのものを押しても編集に入る。
  // 文字を選択してコピーしたときは編集に入らない (選択範囲が残っているかで判定)
  const startByClick = () => {
    if (window.getSelection()?.toString()) return;
    openEditor();
  };
  // 入力欄は値の真上に浮かせて重ねる (Menu と同じく画面の上に出し、下の項目の高さや並びを変えない)。幅は値の幅に合わせる
  const openEditor = () => {
    setAnchorWidth(anchorRef.current?.getBoundingClientRect().width || 0);
    setNotice(null);
    start();
  };
  // 入力欄の外を押したとき。変えていなければ閉じ、変えていれば閉じずに保存か取り消しを促す (打った内容を失わないように)
  const onClickAway = () => {
    if (saving) return;
    if (isEqual(draft, value)) { setEditing(false); setError(null); return; }
    setNotice('保存（✓）か取り消し（✗）を選んでください');
  };

  return (
    <>
      <Box
        ref={anchorRef}
        onClick={editable && !editing ? startByClick : undefined}
        sx={{
          display: 'inline-flex', alignItems: 'center', minWidth: 0, maxWidth: '100%',
          ...(editable ? {
            cursor: 'pointer', borderRadius: 1, px: 0.5, mx: -0.5, transition: 'background-color 0.15s',
            '&:hover': { bgcolor: 'rgba(96, 125, 139, 0.08)' },
            '&:hover .inline-edit-btn, &:focus-within .inline-edit-btn': { opacity: 1 },
            // 編集モードでは、編集できる項目に点線の下線と鉛筆を常に出す (どこを直せるか一目で分かるように)
            ...(mode === 'edit' ? { '& .inline-edit-value': { borderBottom: '1px dashed #90a4ae' }, '& .inline-edit-btn': { opacity: 0.75 } } : {}),
            ...(editing ? { bgcolor: 'rgba(96, 125, 139, 0.12)' } : {}),
          } : {}),
        }}
      >
        {/* 下線の 1px は閲覧中も透明で確保しておき、編集モードに切り替えても項目の高さが変わらないようにする */}
        <Box className="inline-edit-value" sx={{ minWidth: 0, borderBottom: '1px solid transparent' }}>{display}</Box>
        {editable && (
          <Tooltip title={editLabel}>
            <IconButton className="inline-edit-btn" size="small" onClick={(e) => { e.stopPropagation(); if (!editing) openEditor(); }} sx={INLINE_EDIT_ICON_BUTTON_SX} aria-label={editLabel}>
              <Icons.Edit />
            </IconButton>
          </Tooltip>
        )}
      </Box>
      <Popper
        open={editing && !!anchorRef.current} anchorEl={anchorRef.current} placement="bottom-start"
        // 画面に対して置く (fixed)。ページの下端からはみ出してもページの高さが伸びず、スクロールバーが出て画面がずれることがない
        popperOptions={{ strategy: 'fixed' }}
        modifiers={[
          // 入力欄の左上を値の左上に合わせる (Paper の余白 8px ぶん左上へずらす)
          { name: 'offset', options: { offset: ({ reference }: { reference: { height: number } }) => [-8, -reference.height - 8] } },
          { name: 'flip', enabled: false },
          // 画面の下端・右端にかかるときは、画面の内側へ寄せる (縦方向も寄せる)
          { name: 'preventOverflow', options: { padding: 8, altAxis: true } },
        ]}
        // 画面のヘッダー (1100) より上、入力欄の中で開く選択肢のメニュー・候補 (1300) より下
        sx={{ zIndex: 1250 }}
      >
        {/* 外を押したかはボタンを押した瞬間 (mousedown) で判定する。click で判定すると、入力欄の中の選択欄を開いたとき
            押した直後に出るメニューの上で指が離れ、外を押した扱いになってしまう */}
        <ClickAwayListener onClickAway={onClickAway} mouseEvent="onMouseDown">
          {/* 幅は値の幅か最小幅に合わせ、説明文などが長くても横に伸び続けないよう上限を付けて折り返す */}
          <Paper elevation={6} sx={{
            p: 1,
            minWidth: Math.max(anchorWidth + 16, editMinWidth ?? 260),
            maxWidth: `min(${Math.max(anchorWidth + 16, editMinWidth ?? 260, 480)}px, calc(100vw - 32px))`,
          }}>
            <Box sx={{ display: 'flex', alignItems: multiline ? 'flex-start' : 'center', gap: 0.5, width: '100%' }}>
              <Box sx={{ flex: 1, minWidth: 0 }}>{renderEditor({ draft, setDraft, saving, onKeyDown })}</Box>
              {saving ? (
                <LoadingView message="" height={30} size={16} sx={{ width: 56, flexShrink: 0 }} />
              ) : (
                <Box sx={{ display: 'flex', flexShrink: 0 }}>
                  <Tooltip title={multiline ? '保存（Ctrl+Enter）' : '保存（Enter）'}>
                    <IconButton size="small" onClick={save} sx={INLINE_EDIT_SAVE_BUTTON_SX} aria-label="保存"><Icons.Check /></IconButton>
                  </Tooltip>
                  <Tooltip title="取り消し（Esc）">
                    <IconButton size="small" onClick={cancel} sx={INLINE_EDIT_CANCEL_BUTTON_SX} aria-label="取り消し"><Icons.Close /></IconButton>
                  </Tooltip>
                </Box>
              )}
            </Box>
            {error && <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>{error}</Typography>}
            {notice && !error && <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: '#ed6c02' }}>{notice}</Typography>}
          </Paper>
        </ClickAwayListener>
      </Popper>
    </>
  );
}

// 値が空のときの表示
const EmptyText = () => <Typography component="span" variant="body2" color="text.disabled">-</Typography>;

// --------------------------------------------------
// テキスト（1 行・複数行）・日付・年月。前後の空白は落として保存する（空で保存すると値を消す）
// --------------------------------------------------
export function InlineTextField({
  value, onSave, label, display, multiline, required, placeholder, disabled, editMinWidth, type = 'text', validate, normalize,
}: {
  value: string | null | undefined;
  onSave: (next: string) => Promise<void>;
  label?: string;
  display?: React.ReactNode;
  multiline?: boolean;
  required?: boolean;
  placeholder?: string;
  disabled?: boolean;
  editMinWidth?: number;
  type?: 'text' | 'date' | 'month'; // date は YYYY-MM-DD、month は YYYY-MM
  validate?: (v: string) => string | null; // 必須以外の入力チェック（normalize 後の値で呼ぶ）
  normalize?: (v: string) => string; // 保存・比較の前に整える（全角数字を半角に、など）
}) {
  const current = value || '';
  const norm = (v: string) => (normalize ? normalize(v.trim()) : v.trim());
  return (
    <InlineEdit<string>
      // 複数行 (備考など) は文章を書くので、値が短くても入力欄を広めに開く
      value={current} label={label} multiline={multiline} disabled={disabled} editMinWidth={editMinWidth ?? (multiline ? 640 : undefined)}
      display={display ?? (current
        ? <Typography component="span" variant="body2" sx={{ whiteSpace: multiline ? 'pre-wrap' : undefined, wordBreak: 'break-word' }}>{current}</Typography>
        : <EmptyText />)}
      validate={(v) => (required && !norm(v) ? `${label || '値'}は必須です` : validate ? validate(norm(v)) : null)}
      isEqual={(a, b) => norm(a) === norm(b)}
      onSave={(v) => onSave(norm(v))}
      renderEditor={({ draft, setDraft, saving, onKeyDown }) => (
        <TextField
          size="small" fullWidth autoFocus value={draft} disabled={saving} placeholder={placeholder} type={type}
          multiline={multiline} minRows={multiline ? 2 : undefined}
          onChange={(e) => setDraft(e.target.value)} onKeyDown={onKeyDown}
        />
      )}
    />
  );
}

// --------------------------------------------------
// 金額（円）。カンマ付き・全角数字も受け付け、空で保存すると値を消す。表示は呼び出し側の display（fmtYen など）
// --------------------------------------------------
const toHalfWidthNumber = (v: string) => v.replace(/[０-９]/g, (ch) => String.fromCharCode(ch.charCodeAt(0) - 0xfee0)).replace(/[，、]/g, ',');

export function InlineAmountField({ value, onSave, label, display, disabled }: {
  value: number | string | null | undefined;
  onSave: (next: string) => Promise<void>;
  label?: string;
  display?: React.ReactNode;
  disabled?: boolean;
}) {
  return (
    <InlineTextField
      value={value === null || value === undefined ? '' : String(value)} label={label} display={display} disabled={disabled} placeholder="円"
      normalize={toHalfWidthNumber}
      validate={(v) => (/^[\d,]*$/.test(v) ? null : '金額は数字で入力してください')}
      onSave={onSave}
    />
  );
}

// --------------------------------------------------
// 選択。emptyLabel を渡すと「（なし）」を選べる（空文字で保存）。
// 今の値が選択肢に無い（退職者・旧区分など）ときは currentLabel で選択肢に残す
// --------------------------------------------------
export interface InlineOption { value: string; label: string }

export function InlineSelectField({
  value, options, onSave, label, display, currentLabel, emptyLabel, disabled,
}: {
  value: string | null | undefined;
  options: InlineOption[];
  onSave: (next: string) => Promise<void>;
  label?: string;
  display?: React.ReactNode;
  currentLabel?: string;
  emptyLabel?: string;
  disabled?: boolean;
}) {
  const current = value || '';
  const all = current && !options.some((o) => o.value === current) ? [{ value: current, label: currentLabel || current }, ...options] : options;
  const shown = all.find((o) => o.value === current)?.label;
  return (
    <InlineEdit<string>
      value={current} label={label} disabled={disabled}
      display={display ?? (shown ? <Typography component="span" variant="body2">{shown}</Typography> : <EmptyText />)}
      validate={(v) => (emptyLabel === undefined && !v ? `${label || '値'}を選んでください` : null)}
      onSave={onSave}
      renderEditor={({ draft, setDraft, saving, onKeyDown }) => (
        <TextField
          select size="small" fullWidth autoFocus value={draft} disabled={saving}
          onChange={(e) => setDraft(e.target.value)} onKeyDown={onKeyDown}
          slotProps={{ select: { defaultOpen: true, MenuProps: { disableScrollLock: true } } }}
        >
          {emptyLabel !== undefined && <MenuItem value="">{emptyLabel}</MenuItem>}
          {all.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
        </TextField>
      )}
    />
  );
}

// --------------------------------------------------
// 文字列の一覧（案件番号など）。チップで表示し、入力欄では Enter で追加・✕で外す。
// normalize で各要素を整え、空と重複は除く。Enter は追加に使うので、保存は ✓ で行う（Esc で取り消し）
// --------------------------------------------------
export function InlineTagsField({ value, onSave, label, placeholder, helperText, disabled, normalize, editMinWidth }: {
  value: string[] | null | undefined;
  onSave: (next: string[]) => Promise<void>;
  label?: string;
  placeholder?: string;
  helperText?: string;
  disabled?: boolean;
  normalize?: (v: string) => string;
  editMinWidth?: number;
}) {
  const current = value || [];
  const clean = (list: string[]) => {
    const out: string[] = [];
    for (const x of list) { const s = normalize ? normalize(x) : x.trim(); if (s && !out.includes(s)) out.push(s); }
    return out;
  };
  return (
    <InlineEdit<string[]>
      value={current} label={label} disabled={disabled} editMinWidth={editMinWidth ?? 320}
      display={current.length
        ? <Box component="span" sx={{ display: 'inline-flex', gap: 0.5, flexWrap: 'wrap' }}>{current.map((n) => <Chip key={n} size="small" label={n} sx={{ height: 20, fontSize: '0.75rem' }} />)}</Box>
        : <EmptyText />}
      isEqual={(a, b) => clean(a).join('\n') === clean(b).join('\n')}
      onSave={(v) => onSave(clean(v))}
      renderEditor={({ draft, setDraft, saving, onKeyDown }) => (
        <Autocomplete
          multiple freeSolo autoSelect options={[]} value={draft} disabled={saving} size="small" fullWidth
          onChange={(_, v) => setDraft(clean(v as string[]))}
          renderTags={(vals, getTagProps) => vals.map((o, i) => { const { key, ...p } = getTagProps({ index: i }); return <Chip key={key} size="small" label={o} {...p} />; })}
          renderInput={(params) => (
            <TextField {...params} autoFocus placeholder={placeholder} helperText={helperText}
              onKeyDown={(e) => { if (e.key === 'Escape') onKeyDown(e); }} />
          )}
        />
      )}
    />
  );
}
